import React, { Suspense, useEffect, useState } from "react";

import * as dayjs from "dayjs";
import * as dayjsPluginDayOfYear from "dayjs/plugin/dayOfYear";
import * as dayjsPluginTimezone from "dayjs/plugin/timezone";
import * as dayjsPluginUtc from "dayjs/plugin/utc";

import { useTranslation } from "react-i18next";

import { getGeoData } from "../../../api/kquake";
import { get3dFault } from "../../../geoutils";

dayjs.extend(dayjsPluginDayOfYear);
dayjs.extend(dayjsPluginTimezone);
dayjs.extend(dayjsPluginUtc);

const Plot = React.lazy(() => import("react-plotly.js"));

const MARKER_SIZE_MAP_BY_INTENSITY = {
  "II ~ IV": 3,
  IV: 4,
  V: 5,
  VI: 6,
  VII: 7,
  VIII: 8,
  "VIII ~ IX": 9,
};
const PLOTLY_LEGEND_RANK_DEFAULT = 1000;
const FAULT_SURFACE_LEGEND_RANK_BASE = PLOTLY_LEGEND_RANK_DEFAULT + 100;
const FAULT_SURFACE_COLOR_MAP = [
  "rgb(255,69,0)", // OrangeRed
  "rgb(255,00,255)", // Fuchsia
  "rgb(124,252,0)", // LawnGreen
  "rgb(0,255,255)", // Aqua
];

const EARTHQUAKE_MARKER_SCALE_FACTOR = {
  small: 2,
  medium: 3,
  large: 4,
  // xlarge: 4,
};
export const EARTHQUAKE_MARKER_SCALES = Object.keys(
  EARTHQUAKE_MARKER_SCALE_FACTOR
);

export const SeismicityMap = ({
  earthquakes,
  earthquakeMarkerScale,
  historicalEarthquakes,
  faultSurfaces,
}) => {
  const { t } = useTranslation();

  const [canvasSize, setCanvasSize] = useState([0, 0]);
  useEffect(() => {
    if (window.innerWidth < 576) {
      const canvasWidth = window.innerWidth - 10;
      setCanvasSize([canvasWidth, canvasWidth * 1.5]);
    } else if (window.innerWidth < 768) {
      const canvasWidth = window.innerWidth - 40;
      setCanvasSize([canvasWidth, canvasWidth * 1.2]);
    } else if (window.innerWidth < 992) {
      const canvasWidth = window.innerWidth - 60;
      setCanvasSize([canvasWidth, canvasWidth * 1.3]);
    } else if (window.innerWidth < 1200) {
      const canvasWidth = window.innerWidth - 60;
      setCanvasSize([canvasWidth, canvasWidth * 1.3]);
    } else {
      setCanvasSize([1140, 1200]);
    }
  }, []);

  const [majorFaults, setMajorFaults] = useState([]);
  const [geodataTraces, setGeodataTraces] = useState([]);
  useEffect(() => {
    (async () => {
      // setIsLoading(true);
      try {
        const geodata = await getGeoData();
        if (geodata?.faults?.length) {
          setMajorFaults(geodata.faults.filter((fault) => fault.name));
        }
        setGeodataTraces(makeGeodataTraces(geodata, t));
      } catch (error) {
        alert(t("page.interactive-map.alert.dataLoadFailed") + "\r\n" + error);
      }
      // setIsLoading(false);
    })();
  }, []);

  const [earthquakeTrace, setEarthquakeTrace] = useState(null);
  const [earthquakeColorFactor, setEarthquakeColorFactor] =
    useState("ORIGIN_TIME");
  useEffect(() => {
    if (!earthquakes || earthquakes?.length === 0) {
      setEarthquakeTrace(null);
      return;
    }

    setEarthquakeTrace(
      makeEarthquakeTrace(
        earthquakes,
        earthquakeMarkerScale,
        earthquakeColorFactor === "DEPTH"
      )
    );
  }, [earthquakes, earthquakeMarkerScale]);

  // useEffect(() => {
  //   if (!earthquakeTrace) {
  //     return;
  //   }

  //   setEarthquakeTrace({
  //     ...earthquakeTrace,
  //     marker: {
  //       ...earthquakeTrace.marker,
  //       size:
  //     },
  //   });
  // }, [earthquakeMarkerScale]);

  const handleEarthquakeColorFactorChanged = (event) => {
    const isColorByDepth = event.target.checked;
    setEarthquakeColorFactor(isColorByDepth ? "DEPTH" : "ORIGIN_TIME");
    if (!earthquakeTrace) {
      return;
    }
    setEarthquakeTrace({
      ...earthquakeTrace,
      marker: {
        ...earthquakeTrace.marker,
        color: isColorByDepth
          ? earthquakeTrace.__depthes
          : earthquakeTrace.__originTimes,
        reversescale: isColorByDepth,
        colorbar: {
          ...earthquakeTrace.marker.colorbar,
          title: {
            text: isColorByDepth ? "Depth (km)" : "Origin Time (Year)",
            side: "top",
          },
          tickmode: isColorByDepth ? "auto" : "array",
        },
      },
    });
  };

  const [historicalEarthquakeTrace, setHistoricalEarthquakeTrace] = useState();
  useEffect(() => {
    if (!historicalEarthquakes || historicalEarthquakes?.length === 0) {
      setHistoricalEarthquakeTrace(null);
      return;
    }

    setHistoricalEarthquakeTrace(
      makeHistoricalEarthquakeTrace(historicalEarthquakes)
    );
  }, [historicalEarthquakes]);

  const [faultSurfaceTraces, setFaultSurfaceTraces] = useState([]);
  useEffect(() => {
    const newFaultSurfaceTraces = faultSurfaces.map((fault, faultIndex) =>
      makeFaultSurfaceTraces(fault, faultIndex, majorFaults, t)
    );
    console.log(newFaultSurfaceTraces);
    setFaultSurfaceTraces(newFaultSurfaceTraces.flat());
  }, [faultSurfaces]);

  const plotlyData = [...geodataTraces, ...faultSurfaceTraces];
  if (earthquakeTrace) {
    plotlyData.push(earthquakeTrace);
  }
  if (historicalEarthquakeTrace) {
    plotlyData.push(historicalEarthquakeTrace);
  }

  const plotly3DLayout = {
    showlegend: true,
    width: canvasSize[0],
    height: canvasSize[1],
    legend: {
      orientation: "h",
      y: 1.1,
      yanchor: "top",
    },
    margin: {
      l: 10,
      r: 10,
    },
    scene: {
      aspectmode: "manual",
      aspectratio: {
        x: 0.65, // 32.5 ~ 43.5 ~= 790 km
        y: 1, // 123.5 ~ 132.5 ~= 1221 km
        z: 0.27, // -30 ~ 3 = 33 km
      },
      xaxis: {
        title: { text: "Longitude" },
        range: [123.5, 132.5],
      },
      yaxis: {
        title: { text: "Latitude" },
        range: [32.5, 43.5],
      },
      zaxis: {
        title: { text: "Depth (km)" },
        range: [-30, 3],
      },
      camera: {
        eye: { x: 0, y: -1.0, z: 1.25 },
      },
    },
  };

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Plot data={plotlyData} layout={plotly3DLayout} />
      <label
        className="datatable_select_radio"
        htmlFor="event3dDisplay.color.enabled"
      >
        <input
          type="checkbox"
          id="event3dDisplay.color.enabled"
          className="filled-in with-gap"
          name="color.enabled"
          checked={earthquakeColorFactor === "DEPTH"}
          // onChange={(e) => {
          //   setColorByElapsed(e.target.checked);
          // }}
          onChange={handleEarthquakeColorFactorChanged}
        />
        {earthquakeTrace?.__depthes && (
          <span>{t("page.interactive-map.colorMarkersByDepth")}</span>
        )}
      </label>
    </Suspense>
  );
};

const makeGeodataTraces = (geodata, t) => {
  const administrativeRegionTrace = geodata?.adminRegions
    ? {
        type: "scatter3d",
        mode: "lines",
        name: t("w.adminRegions"),
        x: geodata.adminRegions.x,
        y: geodata.adminRegions.y,
        z: new Array(geodata.adminRegions.x.length).fill(0),
        hoverinfo: "skip",
        line: { color: "darkgray" },
        opacity: 0.5,
      }
    : null;

  const demTrace = geodata?.dem
    ? {
        type: "surface",
        name: "DEM",
        // visible: "legendonly",
        showlegend: true,
        opacity: 0.3,
        x: geodata.dem.x,
        y: geodata.dem.y,
        z: geodata.dem.z,
        hoverinfo: "skip",
        colorscale: "Greys", // Earth, Greys, etc. (https://plotly.com/javascript/colorscales/)
        showscale: false,
        cmax: 2,
        cmid: 0,
        cmin: -2,
      }
    : null;

  const faultTraces = geodata?.faults
    ? [
        // Dummy trace
        {
          type: "scatter3d",
          mode: "lines",
          name: t("w.faults"),
          showlegend: true,
          legendgroup: "fault",
          x: [null],
          y: [null],
          z: [null],
          line: { color: "black" },
        },
        ...geodata.faults.map((fault) => {
          const x = fault.lines.reduce(
            (accumulator, currentValue) =>
              accumulator.concat(currentValue.x, [null]),
            []
          );
          const y = fault.lines.reduce(
            (accumulator, currentValue) =>
              accumulator.concat(currentValue.y, [null]),
            []
          );
          return {
            type: "scatter3d",
            mode: "lines",
            name: fault.name ? t("faults." + fault.name) : t("w.faults"),
            showlegend: false,
            legendgroup: "fault",
            x,
            y,
            z: new Array(x.length).fill(0),
            // hoverinfo: fault.name ? "name+x+y" : "x+y",
            line: { color: "black" },
          };
        }),
      ]
    : [];

  return [administrativeRegionTrace, demTrace, ...faultTraces].filter(
    (trace) => trace
  );
};

const makeEarthquakeTrace = (
  earthquakes,
  earthquakeMarkerScale,
  isColorByDepth
) => {
  // console.log("== makeEarthquakeTrace ==");
  const originTimes = earthquakes.map(({ origin }) => origin.time);
  const minOriginTime = Math.min(...originTimes);
  const maxOriginTime = Math.max(...originTimes);
  const firstYear = dayjs.unix(minOriginTime).year();
  const lastYear = dayjs.unix(maxOriginTime).year() + 1;
  const tickInfo = {};
  tickInfo.ticktext = Array.from({ length: lastYear - firstYear + 1 }, (_, i) =>
    (firstYear + i).toString()
  );
  tickInfo.tickvals = tickInfo.ticktext.map((year) =>
    dayjs(`${year}-01-01`).unix()
  );

  const depthes = earthquakes.map(({ origin }) => origin.depth);
  const markerSizes = earthquakes.map(
    ({ magnitude }) =>
      (magnitude._representative
        ? magnitude[magnitude._representative]
        : magnitude.ML) * EARTHQUAKE_MARKER_SCALE_FACTOR[earthquakeMarkerScale]
  );

  return {
    type: "scatter3d",
    name: `Earthquakes (${earthquakes.length})`,
    // showlegend: false,
    x: earthquakes.map(({ origin }) => origin.longitude),
    y: earthquakes.map(({ origin }) => origin.latitude),
    z: earthquakes.map(({ origin }) => origin.depth * -1),
    mode: "markers",
    marker: {
      size: markerSizes,
      color: isColorByDepth ? depthes : originTimes,
      // colorscale: "plasma", // https://plotly.com/javascript/colorscales/
      colorscale: [
        [0, "rgb(0,0,126)"],
        [0.5, "rgb(204,72,119)"],
        [1, "rgb(236,244,112)"],
      ],
      reversescale: isColorByDepth,
      colorbar: {
        title: {
          text: isColorByDepth ? "Depth (km)" : "Origin Time (Year)",
          side: "top",
        },
        len: 0.8,
        orientation: "h",
        tickmode: isColorByDepth ? "auto" : "array",
        ticktext: tickInfo.ticktext, // Not applied if tickmode is "auto"
        tickvals: tickInfo.tickvals, // Not applied if tickmode is "auto"
        y: -0.2,
      },
      // line: {
      //   color: isColorByDepth ? depthes : originTimes,
      //   colorscale: "Bluered", // https://plotly.com/javascript/colorscales/
      //   reversescale: isColorByDepth,
      //   // width: 5, // NOT APPLIED...
      // },
      // symbol: "circle-open",
    },
    text: earthquakes.map((earthquake) => {
      const mag = earthquake.magnitude._representative
        ? earthquake.magnitude[earthquake.magnitude._representative]
        : earthquake.magnitude.ML;
      const magType = earthquake.magnitude._representative || "ML";
      return [
        `Time: ${dayjs
          .unix(earthquake.origin.time)
          .tz("Asia/Seoul")
          .format("YYYY-MM-DDTHH:mm:ss (KST)")}`,
        mag ? `Magnitude: ${mag} (${magType})` : "Magnitude: N/A",
        `Latitude: ${earthquake.origin.latitude}`,
        `Longitude: ${earthquake.origin.longitude}`,
        `Depth(km): ${earthquake.origin.depth}`,
      ].join("<br />");
    }),
    hoverinfo: "text",
    __originTimes: originTimes,
    __depthes: depthes,
  };
};

const makeHistoricalEarthquakeTrace = (earthquakes) => {
  const markerSizes = earthquakes.map(
    ({ intensity }) => MARKER_SIZE_MAP_BY_INTENSITY[intensity]
  );

  return {
    type: "scatter3d",
    name: `Historical Earthquakes (${earthquakes.length})`,
    // showlegend: false,
    x: earthquakes.map(({ longitude }) => longitude),
    y: earthquakes.map(({ latitude }) => latitude),
    z: new Array(earthquakes.length).fill(0),
    mode: "markers",
    marker: {
      symbol: "square-open",
      size: markerSizes,
      // color: earthquakes.map(({ year }) => year),
      // colorscale: [
      //   [0, "rgb(0,0,126)"],
      //   [0.5, "rgb(204,72,119)"],
      //   [1, "rgb(236,244,112)"],
      // ],
      // colorbar: {
      //   title: {
      //     text: "Origin Time (Year)",
      //     side: "top",
      //   },
      //   len: 0.8,
      //   orientation: "h",
      //   y: -0.2,
      // },
      color: "red",
      line: {
        // color: "#FFFFFF",
        color: "red",
        // width: 10,
      },
    },
    text: earthquakes.map((earthquake) =>
      [
        `Date: ${earthquake.date}`,
        `Intensity: ${earthquake.intensity}`,
        `Latitude: ${earthquake.latitude}`,
        `Longitude: ${earthquake.longitude}`,
      ].join("<br />")
    ),
    hoverinfo: "text",
  };
};

const computedTraceMap = [];
const makeFaultSurfaceTraces = (faultDesc, faultIndex, majorFaults, t) => {
  const cached = computedTraceMap.at(faultIndex);
  if (cached?.faultDesc === faultDesc) {
    // console.log(
    //   `makeFaultSurfaceTraces: Returning cached traces for ${faultIndex}`
    // );
    return cached.traces;
  }

  const traces = [];
  if (faultDesc.type === "major") {
    const majorFault = majorFaults.find(
      (major) => major.name === faultDesc.name
    );
    if (!majorFault) {
      return null;
    }
    if (majorFault.lines.length === 1) {
      const xyz = get3dFault(
        majorFault.lines[0].y,
        majorFault.lines[0].x,
        faultDesc.angle,
        faultDesc.depth * -1
      );
      console.log(xyz);
      traces.push({
        type: "surface",
        name: `${t("page.interactive-map.hypoFaultPlane")} (${t(
          "faults." + majorFault.name
        )}) - ${faultIndex + 1}`,
        showlegend: true,
        legendrank: FAULT_SURFACE_LEGEND_RANK_BASE + faultIndex,
        opacity: 0.5,
        x: xyz.x,
        y: xyz.y,
        z: xyz.z,
        // hoverinfo: "name",
        // colorscale: "Viridis",
        // With one color
        colorscale: [
          [
            0,
            FAULT_SURFACE_COLOR_MAP[
              faultIndex % FAULT_SURFACE_COLOR_MAP.length
            ],
          ],
          [
            1,
            FAULT_SURFACE_COLOR_MAP[
              faultIndex % FAULT_SURFACE_COLOR_MAP.length
            ],
          ],
        ],
        showscale: false,
        reversescale: true,
      });
    } else {
      traces.push({
        type: "surface",
        name: `${t("page.interactive-map.hypoFaultPlane")} (${t(
          "faults." + majorFault.name
        )}) - ${faultIndex + 1}`,
        showlegend: true,
        legendrank: FAULT_SURFACE_LEGEND_RANK_BASE + faultIndex,
        legendgroup: `${majorFault.name}-${faultIndex}`,
        opacity: 0.5,
        x: [[null], [null]],
        y: [[null], [null]],
        z: [[null], [null]],
        // hoverinfo: "name",
        // colorscale: "Viridis",
        // With one color
        colorscale: [
          [
            0,
            FAULT_SURFACE_COLOR_MAP[
              faultIndex % FAULT_SURFACE_COLOR_MAP.length
            ],
          ],
          [
            1,
            FAULT_SURFACE_COLOR_MAP[
              faultIndex % FAULT_SURFACE_COLOR_MAP.length
            ],
          ],
        ],
        showscale: false,
        reversescale: true,
      });
      majorFault.lines.forEach((line) => {
        const xyz = get3dFault(
          line.y,
          line.x,
          faultDesc.angle,
          faultDesc.depth * -1
        );
        traces.push({
          type: "surface",
          name: `${t("page.interactive-map.hypoFaultPlane")} (${t(
            "faults." + majorFault.name
          )}) - ${faultIndex + 1}`,
          showlegend: false,
          legendgroup: `${majorFault.name}-${faultIndex}`,
          opacity: 0.5,
          x: xyz.x,
          y: xyz.y,
          z: xyz.z,
          // hoverinfo: "name",
          // colorscale: "Viridis",
          // With one color
          colorscale: [
            [
              0,
              FAULT_SURFACE_COLOR_MAP[
                faultIndex % FAULT_SURFACE_COLOR_MAP.length
              ],
            ],
            [
              1,
              FAULT_SURFACE_COLOR_MAP[
                faultIndex % FAULT_SURFACE_COLOR_MAP.length
              ],
            ],
          ],
          showscale: false,
          reversescale: true,
        });
      });
    }
  } else if (faultDesc.type === "custom") {
    const xyz = get3dFault(
      [faultDesc.startLat, faultDesc.endLat],
      [faultDesc.startLon, faultDesc.endLon],
      faultDesc.angle,
      faultDesc.depth * -1
    );
    traces.push({
      type: "surface",
      name: `${t("page.interactive-map.hypoFaultPlane")} - ${faultIndex + 1}`,
      showlegend: true,
      legendrank: FAULT_SURFACE_LEGEND_RANK_BASE + faultIndex,
      opacity: 0.5,
      x: xyz.x,
      y: xyz.y,
      z: xyz.z,
      // hoverinfo: "name",
      // colorscale: "Viridis",
      // With one color
      colorscale: [
        [
          0,
          FAULT_SURFACE_COLOR_MAP[faultIndex % FAULT_SURFACE_COLOR_MAP.length],
        ],
        [
          1,
          FAULT_SURFACE_COLOR_MAP[faultIndex % FAULT_SURFACE_COLOR_MAP.length],
        ],
      ],
      showscale: false,
      reversescale: true,
    });
  }

  computedTraceMap[faultIndex] = { faultDesc, traces };
  return traces;
};
